import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField
} from '@mui/material';
import { createExportTask } from '../../services/exportService/ExportService';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import i18n from '../../i18n';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

export default function CreateExportTaskDialog({
    open,
    onClose,
    server,
    defaultFrom,
    defaultTo,
    filterRequest,
    searchRequest,
    prefilterRequest,
    onSuccess,
}) {
    const today = useMemo(() => {
        return moment().startOf('day');
    }, []);

    const fourteenDaysAgo = useMemo(() => {
        return moment().startOf('day').subtract(14, 'days');
    }, []);

    const [name, setName] = useState('');
    const [from, setFrom] = useState(defaultFrom || fourteenDaysAgo);
    const [to, setTo] = useState(defaultTo || today);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [validationError, setValidationError] = useState('');

    const formatDate = (date) => {
        return moment(date).format('YYYY-MM-DD');
    };

    const validate = () => {
        if (!from || !to) {
            return 'Both dates are required.';
        }
        if (from > to) {
            return 'Start date must be before or equal to end date.';
        }
        return '';
    };

    const handleSubmit = async () => {
        setError('');
        const v = validate();
        setValidationError(v);
        if (v) return;

        try {
            setSubmitting(true);
            const resp = await createExportTask({
                server,
                dateFrom: formatDate(from),
                dateTo: formatDate(to),
                filterRequest: filterRequest || [],
                searchRequest: searchRequest || [],
                prefilterRequest: prefilterRequest || [],
                name
            });
            setSubmitting(false);
            onClose();
            if (onSuccess) onSuccess(resp);
        } catch (e) {
            setSubmitting(false);
            setError(e.message || 'Unexpected error');
        }
    };

    const handleClose = () => {
        if (submitting) return;
        setError('');
        setValidationError('');
        onClose();
    };

    const locale = i18n.language === 'en' ? 'en-gb' : i18n.language;


    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Create Export Task</DialogTitle>
            <DialogContent>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="Name (optional)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 120 }}
                        />

                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                            <Box>
                                <DatePicker
                                    label="From (inclusive)"
                                    value={from}
                                    onChange={(newValue) => setFrom(newValue)}
                                    slotProps={{
                                        textField: { fullWidth: true, variant: 'outlined' }
                                    }}
                                />
                            </Box>

                            <Box>
                                <DatePicker
                                    label="To (inclusive)"
                                    value={to}
                                    onChange={(newValue) => setTo(newValue)}
                                    slotProps={{
                                        textField: { fullWidth: true, variant: 'outlined' }
                                    }}
                                />
                            </Box>
                        </Box>

                        {validationError ? <Alert severity="warning">{validationError}</Alert> : null}
                        {error ? <Alert severity="error">{error}</Alert> : null}
                    </Stack>
                </LocalizationProvider>
            </DialogContent>

            <DialogActions>
                <Button color="inherit" onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? 'Creating…' : 'Create task'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
